import apiClient from './apiClient';
import type { MaintenanceResult, MaintenanceStatusResult, MaintenanceJobStatus } from '../types';

/**
 * Service para operacoes de manutencao
 */
export const maintenanceService = {
  /**
   * Obtem status das tabelas e indices
   */
  async getStatus(): Promise<MaintenanceStatusResult> {
    const response = await apiClient.get<MaintenanceStatusResult>('/maintenance/status');
    return response.data;
  },

  /**
   * Reindexa dados do Azure Search
   */
  async reindex(confirmationText: string): Promise<MaintenanceResult> {
    const response = await apiClient.post<MaintenanceResult>('/maintenance/reindex', { confirmationText });
    return response.data;
  },

  /**
   * Apaga tabela de eventos
   */
  async deleteEventsData(confirmationText: string): Promise<MaintenanceResult> {
    const response = await apiClient.post<MaintenanceResult>('/maintenance/delete-events-data', { confirmationText });
    return response.data;
  },

  /**
   * Apaga todas as tabelas e indices
   */
  async deleteAll(confirmationText: string): Promise<MaintenanceResult> {
    const response = await apiClient.post<MaintenanceResult>('/maintenance/delete-all', { confirmationText });
    return response.data;
  },

  /**
   * Recalcula metricas de operacoes (apaga tabelas e forca cold start)
   * @deprecated Use startRecalculateOperationsMetrics para operacao assincrona com progresso
   */
  async recalculateOperationsMetrics(confirmationText: string): Promise<MaintenanceResult> {
    const response = await apiClient.post<MaintenanceResult>('/maintenance/recalculate-operations-metrics', { confirmationText });
    return response.data;
  },

  /**
   * Inicia recalculo de metricas de forma assincrona (retorna jobId para polling)
   */
  async startRecalculateOperationsMetrics(confirmationText: string): Promise<MaintenanceJobStatus> {
    const response = await apiClient.post<MaintenanceJobStatus>(
      '/maintenance/recalculate-operations-metrics/start',
      { confirmationText }
    );
    return response.data;
  },

  /**
   * Obtem status de um job de manutencao
   */
  async getJobStatus(jobId: string): Promise<MaintenanceJobStatus> {
    const response = await apiClient.get<MaintenanceJobStatus>(`/maintenance/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Faz polling do job ate conclusao
   */
  async pollJobUntilComplete(
    jobId: string,
    onUpdate: (job: MaintenanceJobStatus) => void,
    intervalMs: number = 2000
  ): Promise<MaintenanceJobStatus> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const job = await this.getJobStatus(jobId);
          onUpdate(job);

          if (job.status === 'completed') {
            resolve(job);
          } else if (job.status === 'failed') {
            reject(new Error(job.errorMessage || 'Job falhou'));
          } else {
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          reject(error);
        }
      };
      poll();
    });
  }
};
